import React, { useState, useRef, useEffect } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import { useData } from "../lib/DataContext";
import { useAuth } from "../lib/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Send, Sparkles, Loader2, Search, Wand2, FileText, Check } from "lucide-react";
import { toast } from "sonner";

const CONF = { High: "bg-emerald-100 text-emerald-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-red-100 text-red-700" };

function Chat() {
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [sid, setSid] = useState(null);
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async () => {
    if (!q.trim() || busy) return;
    const message = q.trim(); setQ(""); setMsgs((m) => [...m, { role: "user", content: message }]); setBusy(true);
    try {
      const { data } = await api.post("/ai/assistant", { message, session_id: sid });
      setSid(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Assistant unavailable"); }
    setBusy(false);
  };
  const suggestions = ["What needs my attention today?", "Which department is the biggest bottleneck?", "Show overdue critical tasks", "What decisions are pending?"];
  return (
    <div className="bg-white border rounded-xl flex flex-col h-[65vh]">
      <div className="flex-1 overflow-auto p-4 space-y-3">
        {msgs.length === 0 && (
          <div className="text-center text-slate-400 mt-10">
            <Sparkles className="mx-auto mb-2" size={28} />
            <div className="text-sm">Ask Task Matrix anything about your work.</div>
            <div className="flex flex-wrap gap-2 justify-center mt-4 max-w-lg mx-auto">
              {suggestions.map((s) => <button key={s} onClick={() => setQ(s)} className="text-xs bg-slate-100 hover:bg-slate-200 rounded-full px-3 py-1.5" data-testid="assistant-suggestion">{s}</button>)}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div data-testid={`chat-msg-${m.role}`} className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-800"}`}>{m.content}</div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-slate-100 rounded-2xl px-4 py-2.5"><Loader2 className="animate-spin" size={16} /></div></div>}
        <div ref={end} />
      </div>
      <div className="border-t p-3 flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask the Task Matrix Assistant…" data-testid="assistant-input" />
        <Button onClick={send} disabled={busy} data-testid="assistant-send"><Send size={16} /></Button>
      </div>
    </div>
  );
}

function NLTask() {
  const { users, projects, departments } = useData();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const parse = async () => {
    if (!text.trim()) return; setBusy(true); setPreview(null);
    try { const { data } = await api.post("/ai/nl-task/parse", { text }); setPreview(data.preview); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not parse"); }
    setBusy(false);
  };
  const create = async () => {
    try {
      const p = preview;
      const owner = p.owner_id || user?.id;
      const { data } = await api.post("/tasks", {
        title: p.title, description: p.description || "", priority: p.priority || "Medium",
        primary_owner_id: owner, current_action_owner_id: owner,
        project_id: p.project_id || undefined, department_id: p.department_id || undefined,
        current_due_date: p.due_date || undefined, original_due_date: p.due_date || undefined,
      });
      if (p.followup_date) { try { await api.post(`/tasks/${data.id}/followups`, { followup_date: p.followup_date, note: "From NL task" }); } catch {} }
      toast.success(`Created ${data.human_id}`); setPreview(null); setText("");
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not create task"); }
  };
  return (
    <div className="max-w-2xl space-y-3">
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid="nltask-input"
        placeholder='e.g. "Create a high priority task for Prasad to complete legal documentation for the Bavdhan project by Friday and follow up Wednesday"' />
      <Button onClick={parse} disabled={busy} data-testid="nltask-parse">{busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <Wand2 size={14} className="mr-1" />}Parse with AI</Button>
      {preview && (
        <div className="bg-white border rounded-xl p-4 space-y-2" data-testid="nltask-preview">
          <div className="flex items-center justify-between"><div className="font-semibold">{preview.title}</div>
            <Badge className={CONF[preview.confidence] || ""}>{preview.confidence} confidence</Badge></div>
          {preview.description && <div className="text-sm text-slate-600">{preview.description}</div>}
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="secondary">Priority: {preview.priority}</Badge>
            {preview.owner_name && <Badge variant="secondary">Owner: {preview.owner_name}</Badge>}
            {preview.project_name && <Badge variant="secondary">Project: {preview.project_name}</Badge>}
            {preview.department_name && <Badge variant="secondary">Dept: {preview.department_name}</Badge>}
            {preview.due_date && <Badge variant="secondary">Due: {preview.due_date}</Badge>}
            {preview.followup_date && <Badge variant="secondary">Follow-up: {preview.followup_date}</Badge>}
          </div>
          <div className="text-[11px] text-amber-600">Review the details above, then confirm to create. Nothing is created without your confirmation.</div>
          <div className="flex gap-2"><Button size="sm" onClick={create} data-testid="nltask-confirm"><Check size={14} className="mr-1" />Create Task</Button>
            <Button size="sm" variant="outline" onClick={() => setPreview(null)}>Discard</Button></div>
        </div>
      )}
    </div>
  );
}

function NLSearch() {
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!text.trim()) return; setBusy(true); setRes(null);
    try { const { data } = await api.post("/ai/nl-search", { text }); setRes(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not search"); }
    setBusy(false);
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2 max-w-2xl">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()}
          placeholder='e.g. "critical tasks overdue more than 5 days"' data-testid="nlsearch-input" />
        <Button onClick={go} disabled={busy} data-testid="nlsearch-go">{busy ? <Loader2 className="animate-spin" size={14} /> : <Search size={14} />}</Button>
      </div>
      {res && (
        <div className="bg-white border rounded-xl overflow-x-auto">
          <div className="px-4 py-2 bg-slate-50 border-b text-sm text-slate-600">{res.interpreted} <span className="text-slate-400">· {res.count} result(s)</span></div>
          <table className="w-full text-sm">
            <tbody>
              {res.tasks.map((t) => (
                <tr key={t.id} className="border-b last:border-0" data-testid="nlsearch-row">
                  <td className="px-4 py-2 font-mono text-xs text-slate-400">{t.human_id}</td>
                  <td className="px-4 py-2 font-medium">{t.title}</td>
                  <td className="px-4 py-2">{t.priority}</td>
                  <td className="px-4 py-2 text-slate-500">{t.status}</td>
                  <td className="px-4 py-2 text-slate-500">{t.days_overdue > 0 ? `${t.days_overdue}d overdue` : t.due}</td>
                </tr>
              ))}
              {res.tasks.length === 0 && <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={5}>No matching tasks.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MeetingExtract() {
  const [text, setText] = useState("");
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState({});
  const run = async () => {
    if (!text.trim()) return; setBusy(true); setOut(null);
    try { const { data } = await api.post("/ai/meeting-extract", { text }); setOut(data.extracted); setSel({}); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not extract"); }
    setBusy(false);
  };
  const createSelected = async () => {
    const items = (out.action_items || []).filter((_, i) => sel[i]);
    if (!items.length) { toast.error("Select at least one action"); return; }
    let n = 0;
    for (const a of items) {
      try { await api.post("/tasks", { title: a.title, priority: a.priority || "Medium",
        primary_owner_id: a.owner_id || undefined, current_action_owner_id: a.owner_id || undefined,
        current_due_date: a.due_date || undefined, original_due_date: a.due_date || undefined,
        description: "Extracted from meeting notes" }); n++; } catch {}
    }
    toast.success(`Created ${n} task(s)`); setOut(null); setText("");
  };
  return (
    <div className="space-y-3">
      <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} data-testid="meeting-input"
        placeholder="Paste meeting notes or transcript here…" className="max-w-3xl" />
      <Button onClick={run} disabled={busy} data-testid="meeting-extract-btn">{busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <FileText size={14} className="mr-1" />}Extract Actions</Button>
      {out && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border rounded-xl p-4">
            <div className="font-semibold mb-2">Action Items <span className="text-xs text-slate-400">(select to create)</span></div>
            <div className="space-y-2">
              {(out.action_items || []).map((a, i) => (
                <label key={i} className="flex items-start gap-2 text-sm border-b last:border-0 pb-2 cursor-pointer" data-testid={`meeting-action-${i}`}>
                  <input type="checkbox" checked={!!sel[i]} onChange={(e) => setSel((s) => ({ ...s, [i]: e.target.checked }))} className="mt-1" />
                  <div className="flex-1"><div className="font-medium">{a.title}</div>
                    <div className="text-xs text-slate-500 flex gap-2 flex-wrap">{a.owner_name && <span>Owner: {a.owner_name}</span>}{a.due_date && <span>Due: {a.due_date}</span>}
                      <span className={`px-1.5 rounded ${CONF[a.confidence] || ""}`}>{a.confidence}</span></div></div>
                </label>
              ))}
              {(out.action_items || []).length === 0 && <div className="text-sm text-slate-400">No action items detected.</div>}
            </div>
            {(out.action_items || []).length > 0 && <Button size="sm" className="mt-3" onClick={createSelected} data-testid="meeting-create-selected"><Check size={14} className="mr-1" />Create Selected Tasks</Button>}
          </div>
          <div className="space-y-3">
            {[["Decisions", out.decisions], ["Risks", out.risks], ["Issues", out.issues], ["Follow-ups", out.followups]].map(([label, arr]) => (
              <div key={label} className="bg-white border rounded-xl p-4">
                <div className="font-semibold mb-1 text-sm">{label}</div>
                <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                  {(arr || []).map((x, i) => <li key={i}>{x.text || x.title}{x.due_date ? ` (${x.due_date})` : ""}</li>)}
                  {(arr || []).length === 0 && <li className="list-none text-slate-400">None</li>}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Assistant() {
  return (
    <div className="p-6">
      <PageHeader title="Talk to Task Matrix" subtitle="Ask questions, create tasks in plain English, search naturally, and turn meeting notes into actions" />
      <Tabs defaultValue="chat">
        <TabsList className="mb-4">
          <TabsTrigger value="chat" data-testid="tab-chat"><Sparkles size={15} className="mr-1" />Assistant</TabsTrigger>
          <TabsTrigger value="task" data-testid="tab-nltask"><Wand2 size={15} className="mr-1" />Create Task</TabsTrigger>
          <TabsTrigger value="search" data-testid="tab-nlsearch"><Search size={15} className="mr-1" />Search</TabsTrigger>
          <TabsTrigger value="meeting" data-testid="tab-meeting"><FileText size={15} className="mr-1" />Meeting Extract</TabsTrigger>
        </TabsList>
        <TabsContent value="chat"><Chat /></TabsContent>
        <TabsContent value="task"><NLTask /></TabsContent>
        <TabsContent value="search"><NLSearch /></TabsContent>
        <TabsContent value="meeting"><MeetingExtract /></TabsContent>
      </Tabs>
    </div>
  );
}
